import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';

import { NEVER, Observable, of, map, tap, switchMap } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { CmsCollection, CmsOffer } from '@fcom/core-api';
import {
  PageMetaService,
  PriceCalendarCTAParams,
  PriceCalendarParams,
  PriceCalendarService,
  PromotionItem,
  PromotionType,
} from '@fcom/common';
import { GlobalBookingTravelClass, TripType } from '@fcom/core/interfaces';
import { GtmService } from '@fcom/common/gtm';
import { getOfferWithAmDestination } from '@fcom/common/utils';
import { AmDestinationService } from '@fcom/destination-search/services/am-destination.service';
import { LocationRouteCffService, stopPropagation } from '@fcom/core';

import { BlockOffer, BlockOfferData } from '../../interfaces';
import { toPromoBlock } from '../../services';

@Component({
  selector: 'cms-offers-grid',
  template: `
    <fin-offer-grid
      *ngIf="originLocationCode$ | async as originLocationCode"
      [title]="content.teaserTitle"
      [offers$]="offers$"
      [cmsTags]="content.subjectTaxonomyTags || []"
      (flightHotelClicked)="openAmContinueModal($event.amLink)"
      (calendarClicked)="calendarClicked(originLocationCode, $event)"
      [tripType]="tripType"
    />
    <fin-am-destination-continue-modal
      [open]="isAmContinueModalOpen"
      (closeModal)="closeAmContinueModal()"
      [bookUrl]="flightHotelUrl"
    />
    <fcom-modal
      [open]="(calendarOpen$ | async) !== null"
      [fitToContent]="true"
      [buttons]="null"
      [roundedContent]="true"
      [fullHeight]="true"
      [extraWide]="true"
      (close)="closePriceCalendar()"
      data-testid="price-calendar-modal"
    >
      <fin-price-calendar
        [openWithParams$]="calendarOpen$"
        [showAddReturn]="true"
        [showSubtitle]="true"
        [ctaLabel]="'bookingSearch.search' | finLocalization"
        (modalClose)="closePriceCalendar()"
        (ctaClicked)="priceCalendarCTAClicked($event)"
      />
    </fcom-modal>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CmsOffersGridComponent implements OnInit {
  private languageService = inject(LanguageService);
  private gtmService = inject(GtmService);
  private amDestinationService = inject(AmDestinationService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private priceCalendarService = inject(PriceCalendarService);
  private pageMetaService = inject(PageMetaService);

  @Input()
  content: CmsCollection;

  offers$: Observable<BlockOfferData[]>;
  tripType: TripType;
  flightHotelUrl: string;
  isAmContinueModalOpen = false;
  calendarOpen$: Observable<PriceCalendarParams | null> = NEVER;
  originLocationCode$: Observable<string>;

  constructor() {
    this.calendarOpen$ = this.priceCalendarService.calendarOpen$;
    this.originLocationCode$ = this.pageMetaService.defaultOriginLocationCode$;
  }

  ngOnInit(): void {
    this.offers$ = this.languageService.lang.pipe(
      map((lang) => this.content.items.map((offer) => toPromoBlock(offer as CmsOffer, lang))),
      switchMap((offers) =>
        this.languageService.localeValue.includes('FI')
          ? getOfferWithAmDestination(
              offers,
              this.amDestinationService.amDestinationList(),
              this.locationRouteCffService
            )
          : of(offers)
      ),
      tap((offers) => {
        const promotionItems: PromotionItem[] = offers.map((offer, index) => ({
          position: `${index}`,
          id: 'top-destinations',
          product: offer.destination,
          type: 'static',
        }));

        this.gtmService.internalPromotion(promotionItems, PromotionType.VIEW);
      })
    );

    const tripType = this.content.localSettings?.tripType as string | undefined;
    this.tripType = TripType[tripType?.toUpperCase()] ?? TripType.RETURN;
  }

  openAmContinueModal(flightHotelUrl: string): void {
    this.isAmContinueModalOpen = true;
    this.flightHotelUrl = flightHotelUrl;
  }

  closeAmContinueModal(): void {
    this.isAmContinueModalOpen = false;
  }

  calendarClicked(
    selectedOriginLocationCode: string,
    [event, destinationItem, travelClass, tripType]: [Event, BlockOffer, GlobalBookingTravelClass, TripType, number]
  ): void {
    stopPropagation(event);
    this.priceCalendarService.openPriceCalendar(
      selectedOriginLocationCode,
      destinationItem.destination,
      tripType,
      travelClass,
      destinationItem.title,
      destinationItem.discountCode
    );
  }

  priceCalendarCTAClicked(params: PriceCalendarCTAParams): void {
    this.priceCalendarService.priceCalendarCTAClicked(params);
  }

  closePriceCalendar(): void {
    this.priceCalendarService.closePriceCalendar();
  }
}
